import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useModal } from 'components/modals/ModalContext';
import { useFetchData } from 'services/api/useFetchData';
import { useSettings } from 'services/settings/SettingsContext';
import { formatDate } from 'services/utils/dateTimeUtils';
import { PlatformUpdate } from '../contacts/newsletters/NewsletterTypes';
import '../../style/scss/newsletters.scss';

interface PlatformUpdateModalProps {
    updateId: number;
}

// Shows a platform update of the dashboard: the sent release newsletter rendered in its layout
const PlatformUpdateModal: React.FC<PlatformUpdateModalProps> = ({ updateId }) => {
    const { t } = useTranslation();
    const { userLocale } = useSettings();
    const { revealModal } = useModal();
    const shadowRootRef = useRef<HTMLDivElement>(null);

    // Fetch the update with its rendered html
    const { response } = useFetchData({ apiUrl: `get_platform_update/${updateId}` });
    const update = response as PlatformUpdate | null;

    // Put the rendered html in a shadow root so the email styles stay isolated, then reveal the modal
    useEffect(() => {
        if (!shadowRootRef.current || !update?.html) return;
        const shadowRoot = shadowRootRef.current.shadowRoot ?? shadowRootRef.current.attachShadow({ mode: 'open' });
        shadowRoot.innerHTML = update.html;
        revealModal();
    }, [update]);

    return (
        <div className='platform-update-modal'>
            <h4>{update ? t('dashboard.platform_update.title', { number: update.release_number, date: formatDate(update.sent_at, userLocale, 'writtenMonth') }) : ''}</h4>
            <div className='platform-update-frame' ref={shadowRootRef} />
        </div>
    );
};

export default PlatformUpdateModal;
